import { type RefObject } from 'react'
import styled from 'styled-components'
import { CATEGORY_HEADER_TYPE } from '@/features/shared/constants/global'
import { type ShoppingCategory } from '@/features/shop/services/ShoppingCategories/types'
import { screenSizes } from '@shipt/design-system-themes'
import { boxShadow } from '@/features/shared/styles/effects'
import { MoreCategories } from '@/features/shop/components/CategorySubNav/MoreCategories'
import {
  type KeyboardHandler,
  type OnCategorySelect,
} from '@/features/shop/components/CategorySubNav/types'
import { CategoryH2 } from '@/features/shop/components/CategorySubNav/CategoryH2'
import { Stack } from '@shipt/design-system-layouts'
import { GridLayoutContainer } from '@/features/shared/styles/layout'

type Props = {
  categoryDrawerRef: RefObject<HTMLDivElement | null>
  selectedCategory: ShoppingCategory | undefined
  onCategorySelect: OnCategorySelect
  drawerIsVisible: boolean
  moreCategories: ShoppingCategory[]
  featuredCategories: ShoppingCategory[]
  keyboardHandler: KeyboardHandler
  selectedOption: string
}

export const CategoriesDrawer = ({
  categoryDrawerRef,
  selectedCategory,
  onCategorySelect,
  drawerIsVisible = false,
  moreCategories = [],
  keyboardHandler,
  featuredCategories,
  selectedOption,
}: Props) => {
  const isMoreSelected = selectedOption === CATEGORY_HEADER_TYPE.MORE
  const openDrawerStyles = Boolean(selectedCategory?.children) || isMoreSelected

  return (
    <Wrapper drawerIsVisible={openDrawerStyles} data-is-more-selected={isMoreSelected}>
      <GridLayoutContainer>
        <ContentContainer
          wrap
          childrenCount={selectedCategory?.children?.length || 0}
          ref={categoryDrawerRef}
          role="menu"
        >
          {isMoreSelected ? (
            <MoreCategories
              featuredCategories={featuredCategories}
              moreCategories={moreCategories}
              onCategorySelect={onCategorySelect}
              drawerIsVisible={drawerIsVisible}
            />
          ) : (
            selectedCategory?.children
              ?.slice(0, 6)
              .map((subCategory, columnIndex) => (
                <CategoryH2
                  key={subCategory.id}
                  subCategory={subCategory}
                  columnIndex={columnIndex}
                  onCategorySelect={onCategorySelect}
                  keyboardHandler={keyboardHandler}
                />
              ))
          )}
        </ContentContainer>
      </GridLayoutContainer>
    </Wrapper>
  )
}

const ContentContainer = styled(Stack)<{
  childrenCount: number
}>`
  justify-content: ${({ childrenCount }) => (childrenCount > 4 ? 'space-between' : 'space-around')};
`

const Wrapper = styled.div<{
  'data-is-more-selected': boolean
  drawerIsVisible: boolean
}>`
  display: none;

  @media ${screenSizes.tablet} {
    display: block;
  }

  z-index: -1;
  position: absolute;
  width: 100%;
  background-color: ${({ theme }) => theme.color.background.base.neutral.subtle_00};
  padding: ${({ drawerIsVisible }) => (drawerIsVisible ? '3.5rem 4rem 1.5rem' : '0 4rem')};
  box-shadow: ${({ drawerIsVisible }) => (drawerIsVisible ? boxShadow : 'none')};
  transform: translateY(
    ${({ drawerIsVisible }) => (drawerIsVisible ? '0px' : '-1000px')}
  );
  transition: transform 0.45s ease-in-out;
  left: 0;

  @media ${screenSizes.tablet} and (max-width: 1000px) {
    &[data-is-more-selected='true'] {
      height: 600px;
      max-height: 600px;
      overflow-y: auto;
      overscroll-behavior: contain;
      width: 100%;
      right: unset;
      padding: 56px 0 0;
    }
  }
`
